import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Platform,
  ActivityIndicator,
  Alert,
} from 'react-native';

import { Header } from '../components/Header';
import {
  FileText,
  Save,
  RotateCcw,
  Download,
  Upload,
} from '../components/icons';
import {
  KeyboardDismissToolbar,
  ACCESSORY_VIEW_ID,
} from '../components/shared/KeyboardDismissToolbar';
import { SubHeader } from '../components/SubHeader';
import { useAuth } from '../contexts/AuthContext';
import { roleService } from '../services/auth/roleService';
import {
  PromptsService,
  PromptType,
  PromptData,
} from '../services/promptsService';
import { dateFormatting } from '../utils/dateFormatting';

const isWeb = Platform.OS === 'web';

interface PromptManagementProps {
  navigate: (path: string) => void;
  currentPath?: string;
}

export const PromptManagement: React.FC<PromptManagementProps> = ({
  navigate,
  currentPath,
}) => {
  const { user } = useAuth();
  const [selectedType, setSelectedType] = useState<PromptType>('ocr');
  const [promptContent, setPromptContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [isDefault, setIsDefault] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<string | undefined>();
  const [updatedByEmail, setUpdatedByEmail] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // File input ref for upload (web only)
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Check if user can access prompt management (IT Admin only)
  const canAccessPromptManagement = roleService.canAccessPromptManagement(user);

  // Redirect if not authorized
  useEffect(() => {
    if (user && !canAccessPromptManagement) {
      navigate('/unauthorized');
    }
  }, [user, canAccessPromptManagement, navigate]);

  // Load prompt when type changes
  useEffect(() => {
    if (canAccessPromptManagement) {
      loadPrompt(selectedType);
    }
  }, [selectedType, canAccessPromptManagement]);

  const loadPrompt = async (type: PromptType) => {
    try {
      setLoading(true);
      const prompt = await PromptsService.getPrompt(type);
      setPromptContent(prompt.content);
      setOriginalContent(prompt.content);
      setIsDefault(prompt.isDefault);
      setUpdatedAt(prompt.updatedAt);
      setUpdatedByEmail(prompt.updatedByEmail);
    } catch (error) {
      console.error('Error loading prompt:', error);
      Alert.alert('Error', 'Failed to load prompt. Please try again.', [
        { text: 'OK' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (promptContent.trim().length === 0) {
      Alert.alert('Error', 'Prompt content cannot be empty.');
      return;
    }

    try {
      setSaving(true);
      const result = await PromptsService.setPrompt(
        selectedType,
        promptContent,
      );
      setOriginalContent(result.content);
      setIsDefault(result.isDefault);
      setUpdatedAt(result.updatedAt);
      setUpdatedByEmail(result.updatedByEmail);
      Alert.alert(
        'Success',
        `${selectedType.toUpperCase()} prompt saved successfully.`,
      );
    } catch (error) {
      console.error('Error saving prompt:', error);
      Alert.alert('Error', 'Failed to save prompt. Please try again.', [
        { text: 'OK' },
      ]);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (isDefault) {
      Alert.alert('Info', 'This prompt is already using the default.');
      return;
    }

    Alert.alert(
      'Reset to Default',
      `Are you sure you want to reset the ${selectedType.toUpperCase()} prompt to the default? This will delete your custom prompt.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Reset',
          style: 'destructive',
          onPress: async () => {
            try {
              setResetting(true);
              const result = await PromptsService.resetPrompt(selectedType);
              setPromptContent(result.content);
              setOriginalContent(result.content);
              setIsDefault(true);
              setUpdatedAt(undefined);
              setUpdatedByEmail(undefined);
              Alert.alert(
                'Success',
                `${selectedType.toUpperCase()} prompt reset to default.`,
              );
            } catch (error) {
              console.error('Error resetting prompt:', error);
              Alert.alert(
                'Error',
                'Failed to reset prompt. Please try again.',
                [{ text: 'OK' }],
              );
            } finally {
              setResetting(false);
            }
          },
        },
      ],
    );
  };

  const handleDownload = () => {
    if (!isWeb) {
      Alert.alert('Info', 'Download is only available on web.');
      return;
    }

    try {
      const fileName = `${selectedType}_prompt_${
        new Date().toISOString().split('T')[0]
      }.txt`;
      const blob = new Blob([promptContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading prompt:', error);
      Alert.alert('Error', 'Failed to download prompt.');
    }
  };

  const handleUpload = () => {
    if (!isWeb) {
      Alert.alert('Info', 'Upload is only available on web.');
      return;
    }

    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.txt') && file.type !== 'text/plain') {
      Alert.alert('Error', 'Please select a .txt file.');
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      const content = e.target?.result as string;
      if (content) {
        setPromptContent(content);
        Alert.alert(
          'Success',
          'Prompt loaded from file. Click "Save Changes" to apply.',
        );
      }
    };
    reader.onerror = () => {
      Alert.alert('Error', 'Failed to read file.');
    };
    reader.readAsText(file);

    // Reset file input to allow selecting same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const hasChanges = promptContent !== originalContent;

  // Don't render if not authorized
  if (!canAccessPromptManagement) {
    return null;
  }

  if (loading) {
    return (
      <View style={styles.container}>
        <Header
          currentPath={currentPath || '/prompt-management'}
          navigate={navigate}
        />
        <SubHeader title="Prompt Management" onNavigate={navigate} />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#FF6B35" />
          <Text style={styles.loadingText}>Loading prompt...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Header
        currentPath={currentPath || '/prompt-management'}
        navigate={navigate}
      />
      <SubHeader title="Prompt Management" onNavigate={navigate} />

      <ScrollView style={styles.content}>
        {/* Prompt Type Selector */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <FileText size={20} color="#666" />
            <Text style={styles.sectionTitle}>Select Prompt Type</Text>
          </View>

          <View style={styles.sectionContent}>
            <View style={styles.tabContainer}>
              <TouchableOpacity
                style={[styles.tab, selectedType === 'ocr' && styles.tabActive]}
                onPress={() => setSelectedType('ocr')}
                disabled={saving || resetting}
              >
                <Text
                  style={[
                    styles.tabText,
                    selectedType === 'ocr' && styles.tabTextActive,
                  ]}
                >
                  OCR (Documents)
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.tab,
                  selectedType === 'audio' && styles.tabActive,
                ]}
                onPress={() => setSelectedType('audio')}
                disabled={saving || resetting}
              >
                <Text
                  style={[
                    styles.tabText,
                    selectedType === 'audio' && styles.tabTextActive,
                  ]}
                >
                  Audio (Transcription)
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* Prompt Status */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <View style={styles.statusContainer}>
              {isDefault ? (
                <View style={styles.statusBadgeDefault}>
                  <Text style={styles.statusBadgeText}>Using Default</Text>
                </View>
              ) : (
                <View style={styles.statusBadgeCustom}>
                  <Text style={styles.statusBadgeText}>Custom Prompt</Text>
                </View>
              )}
              {hasChanges && (
                <View style={styles.statusBadgeUnsaved}>
                  <Text style={styles.statusBadgeText}>Unsaved Changes</Text>
                </View>
              )}
            </View>
          </View>

          {!isDefault && updatedAt && (
            <View style={styles.metadataContainer}>
              <Text style={styles.metadataText}>
                Last updated:{' '}
                {dateFormatting.format(updatedAt, {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                  second: 'numeric',
                }) ?? ''}
                {updatedByEmail && ` by ${updatedByEmail}`}
              </Text>
            </View>
          )}
        </View>

        {/* Prompt Editor */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>
              {selectedType === 'ocr'
                ? 'OCR System Prompt'
                : 'Audio System Prompt'}
            </Text>
          </View>

          <View style={styles.editorContainer}>
            <TextInput
              style={styles.textEditor}
              value={promptContent}
              onChangeText={setPromptContent}
              multiline
              placeholder="Enter the system prompt here..."
              placeholderTextColor="#999"
              editable={!saving && !resetting}
              inputAccessoryViewID={
                Platform.OS === 'ios' ? ACCESSORY_VIEW_ID : undefined
              }
            />
          </View>
        </View>

        {/* Download/Upload Buttons (Web Only) */}
        {isWeb && (
          <View style={styles.fileActionsContainer}>
            <TouchableOpacity
              style={styles.fileActionButton}
              onPress={handleDownload}
              disabled={saving || resetting}
            >
              <Download size={18} color="#666" />
              <Text style={styles.fileActionButtonText}>Download as .txt</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.fileActionButton}
              onPress={handleUpload}
              disabled={saving || resetting}
            >
              <Upload size={18} color="#666" />
              <Text style={styles.fileActionButtonText}>Upload .txt File</Text>
            </TouchableOpacity>

            {/* Hidden file input for upload */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,text/plain"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </View>
        )}

        {/* Action Buttons */}
        <View style={styles.actionContainer}>
          <TouchableOpacity
            style={[
              styles.saveButton,
              (!hasChanges || saving) && styles.buttonDisabled,
            ]}
            onPress={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Save size={18} color="#fff" />
            )}
            <Text style={styles.saveButtonText}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.resetButton,
              (isDefault || resetting) && styles.buttonDisabled,
            ]}
            onPress={handleReset}
            disabled={isDefault || resetting}
          >
            {resetting ? (
              <ActivityIndicator size="small" color="#dc3545" />
            ) : (
              <RotateCcw size={18} color={isDefault ? '#999' : '#dc3545'} />
            )}
            <Text
              style={[
                styles.resetButtonText,
                isDefault && styles.resetButtonTextDisabled,
              ]}
            >
              {resetting ? 'Resetting...' : 'Reset to Default'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Character Count */}
        <View style={styles.charCountContainer}>
          <Text style={styles.charCountText}>
            {promptContent.length.toLocaleString()} characters
          </Text>
        </View>
      </ScrollView>

      {/* iOS Keyboard Dismiss Toolbar */}
      <KeyboardDismissToolbar />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#d1ecf1',
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
    gap: 12,
  },
  infoBannerText: {
    flex: 1,
    fontSize: 14,
    color: '#0c5460',
    lineHeight: 20,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
    marginBottom: 20,
    overflow: 'hidden',
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
    gap: 12,
    backgroundColor: '#f8f9fa',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  sectionContent: {
    padding: 16,
  },
  tabContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#fff',
    alignItems: 'center',
  },
  tabActive: {
    borderColor: '#FF6B35',
    backgroundColor: '#FFF5F2',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
  },
  tabTextActive: {
    color: '#FF6B35',
  },
  statusContainer: {
    flexDirection: 'row',
    gap: 8,
    flex: 1,
  },
  statusBadgeDefault: {
    backgroundColor: '#e3f2fd',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  statusBadgeCustom: {
    backgroundColor: '#e8f5e9',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  statusBadgeUnsaved: {
    backgroundColor: '#fff3e0',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
  },
  metadataContainer: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  metadataText: {
    fontSize: 13,
    color: '#666',
    fontStyle: 'italic',
  },
  editorContainer: {
    padding: 0,
  },
  textEditor: {
    minHeight: 400,
    padding: 16,
    fontSize: 14,
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier',
    color: '#333',
    backgroundColor: '#fafafa',
    textAlignVertical: 'top',
    lineHeight: 22,
    ...(Platform.OS === 'web'
      ? {
          outlineStyle: 'none',
          resize: 'vertical',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }
      : {}),
  },
  fileActionsContainer: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  fileActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8f9fa',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    gap: 8,
  },
  fileActionButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
  },
  actionContainer: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 20,
  },
  saveButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FF6B35',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    gap: 8,
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  resetButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#dc3545',
    gap: 8,
  },
  resetButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#dc3545',
  },
  resetButtonTextDisabled: {
    color: '#999',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  charCountContainer: {
    alignItems: 'flex-end',
    marginBottom: 40,
  },
  charCountText: {
    fontSize: 13,
    color: '#999',
  },
});
